/* eslint-disable camelcase */

import { TabName } from 'components/TCDashboardTab/types';

import {
  ClinicalAssessmentResult,
  LinkReason,
  DischargeReason,
  TriggerCodes,
} from '..';

export enum SpecialistType {
  CP = 'CP',
  TC = 'TC',
}

export enum SpecialistRole {
  CP = 'specialist',
  TC = 'therapy-companion',
}

export interface Author {
  name: string;
  specialistType: SpecialistType;
}

export interface NotesProps {
  id: string;
  title: string;
  createdAt: string;
  updatedAt: string;
  description: string;
  author: Author;
}

export interface NotesRequestProps {
  results: Array<NotesProps>;
  metadata: {
    currentItemsPage: number;
    totalPages: number;
    nextCursor: string;
  };
}

export interface LogbookProps {
  id: string;
  title: string;
  createdAt: string;
  updatedAt: string;
  description: string;
  author: Author;
}

export interface LogbookRequestProps {
  results: Array<LogbookProps>;
  metadata: {
    currentItemsPage: number;
    totalPages: number;
    nextCursor: string;
  };
}

export interface SpecialistInfo {
  id: string;
  fullName: string;
  email: string;
  phoneNumber: string;
  birthday: string;
  socialLink: string;
  socialUsername: string;
  gender: string;
  locale: string;
  videoId: string;
  avatar: string;
  countryId: string;
  geoCode: string;
}

export interface Speciality {
  titleKey: string;
}

export interface SpecialistProfessionalInfo {
  type?: SpecialistType;
  professionalNumber: string;
  specialities: Speciality[];
  aboutYou: string;
  epsiNumber: string;
  education: string;
}

export interface Metadata {
  nextCursor: string;
  totalPages: number;
}

export interface SpecialistResponse {
  full_name: string;
  cognito_id: string;
}

export interface PatientResponse {
  id: string;
  full_name: string;
  user_id: string;
  cognito_id: string;
  patient_attention: boolean;
  total_session: number;
  total_credits: number;
  last_session: string;
  created_at: string;
  notifies_renewal: boolean;
}

export interface GetPatientsResponse {
  metadata: Metadata;
  results: PatientResponse[];
}

export const enum ActionType {
  UPDATED = 'update-session',
  CREATED = 'create-session',
}

export interface SessionHistoryResponse {
  status: string;
  start_at: string;
  CreatedAt: string;
  session_id: string;
  end_user_id: string;
  action_type: ActionType;
  initial_session?: boolean;
  end_user_full_name: string;
  end_user_cognito_id: string;
  invite_status: 'accepted' | 'sent' | null;
}

export interface ScheduledMessage {
  id: number;
  final_message: string;
  status: 'scheduled' | 'sent';
  schedule_time: string;
  trigger: {
    trigger_code: TriggerCodes;
  };
}

export interface UserResponse {
  id: string;
  cognito_id: string;
  full_name: string;
  expired_at?: string;
  is_critical: boolean;
}

export interface ChatResponse {
  conversation_id: string;
  has_chat_message: boolean;
  has_unreaded_message: boolean;
  total_unreaded_message?: number;
  has_enduser_interaction?: boolean;
  last_message_delivered_at?: string;
  has_scheduled_messages: boolean;
  scheduled_messages: ScheduledMessage[];
}

export interface ChatKey {
  key: string;
  active: boolean;
  version: number;
}

export type ChatKeysResponse = ChatKey[];

export interface SpecialistChatUser {
  user: UserResponse;
  chat: ChatResponse;
  keys: ChatKeysResponse;
}

export interface SpecialistChatResponse {
  metadata: Metadata;
  results: SpecialistChatUser[];
}

export type SpecialistDashboardTabs = Record<TabName, number>;

export interface SpecialistDashboardOverview extends SpecialistDashboardTabs {
  active_users_amount: number;
}

export type OriginType = 'ohmyform' | 'cp';

export type ReasonType = 'eligibility_shutdown' | 'company_shutdown';

export interface CriticalResponse {
  id: string;
  user_id: string;
  cognito_id: string;
  full_name: string;
  criticality: string;
  next_session: string;
  classification_date: string;
  is_confirmed: boolean;
  origin: OriginType;
}

export interface UnlinkResponse {
  cognito_id: string;
  full_name: string;
  reason: ReasonType;
  user_expired_at: string;
  user_id: string;
}

export interface CriticalMetadataResponse {
  total_critical_users: number;
  total_users: number;
}

export interface UnlinkMetadataResponse {
  total_unlinks: number;
}

export interface SpecialistDashboardCritical {
  data: Array<CriticalResponse>;
  metadata: CriticalMetadataResponse;
}

export interface SpecialistDashboardUnlink {
  Data: Array<UnlinkResponse>;
  Metadata: UnlinkMetadataResponse;
}

export enum UsersFilterName {
  NEW = 'new_users',
  ENGAGED = 'engaged_users',
  INACTIVE = 'inactive_users',
  ACTIVE_USERS = 'active_users',
  DISENGAGED = 'disengaged_users',
  NOT_VINCULATED = 'unlinked_users',
  NOT_INITIALIZED = 'not_started_users',
  FINISHING_PROGRAM = 'finishing_program_users',
  WARNING = 'warning_users',
}

export interface SpecialistDashboardUser {
  origin?: string;
  user_id?: string;
  full_name?: string;
  cognito_id?: string;
  created_at?: string;
  phq9_value?: number;
  gad7_value?: number;
  phq9_result?: ClinicalAssessmentResult;
  gad7_result?: ClinicalAssessmentResult;
  unlinked_at?: string;
  therapist_name?: string;
  total_pages?: number;
  acknowledged?: number;
  source_link?: LinkReason;
  last_message_sent_at?: string;
  discharge_reason?: DischargeReason;
  last_activity_finished_at?: string;
}
export interface GetSessionHistoryResponse {
  results: SessionHistoryResponse[];
  metadata: Metadata;
}

type Completed = {
  initial_session_amount?: number;
  regular_session_amount?: number;
  accepted_session_amount?: number;
};

export interface GetSessionHistorySummaryResponse {
  total_sessions: number;
  late_cancel: number;
  no_show: number;
  completed: Completed;
}

export type Limit = '5' | '10' | '20' | '40' | '80' | '160' | '320';

export interface Discharge {
  dischargeReason: string;
  dischargeDescription: string;
}

export interface AllowDayAndTime {
  allow_day_and_time?: {
    day: string;
    end: string;
    start: string;
  };
  allow_period?: {
    start: string;
    end: string;
  };
  block_period?: {
    start: string;
    end: string;
  };
}

export interface SpecialistAvailability {
  id?: string;
  first_name?: string;
  last_name?: string;
  name?: string;
  timezone?: string;
  image?: string;
  email?: string;
  availability_constraints?: AllowDayAndTime[];
}

export interface SpecialistSession {
  id: string;
  booking_id: string;
  status: string;
  end_user_cognito_id: string;
  end_user_full_name: string;
  start_at: string;
  end_at: string;
  can_join: boolean;
  can_join_after_date: string;
  rescheduled_session: boolean;
}

export interface SpecialistSessionResponse {
  data: SpecialistSession[];
}

export interface GetSessionLinkResponse {
  url: string;
}

export interface ChatStatus {
  user_specialist_id: string;
  has_unread_message: boolean;
}

export interface HideSpecialistInfo {
  can_receive_new_users: boolean;
  can_toggle_to_receive_new_users: boolean;
}

export interface MfaConfig {
  cognito_id: string;
  enable_mfa: string;
  limit_date_disabled: string;
  phone_number: string;
}

export interface GetSpecialistChatUsersParams {
  userName?: string;
  hasChatMessage?: boolean;
  hasPatientInteraction?: boolean;
  hasScheduledMessage?: boolean;
  hasUnreadMessage?: boolean;
  nextCursor?: string;
  cognitoId?: string;
}

export type InviteStatus = 'pending' | 'accepted' | 'rejected' | 'expired';

export interface Invites {
  end_user_full_name: string;
  user_id: string;
  slot_date: string;
  status: InviteStatus;
  rescheduled_session: {
    start_at: string;
  } | null;
  expired_at: string;
  url: string;
}

export interface GetBookingInvites {
  results: Invites[];
  metadata: {
    totalPages: number;
    nextCursor: string;
    totalItems: number;
  };
}

export type ErrorInvite = 'PAST_DATE' | 'LIMIT_INVITE_DATE';
